import React, { useEffect, useRef, useState } from 'react';
import { ChefHat } from 'lucide-react';

const CarteSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const carte = {
    entreesPartager: [
      { name: "Oeufs Mimosa à la Truffe", nameEn: "Truffle Mimosa Eggs", price: "16€" },
      { name: "St Marcelin Rôti au Miel, Lardon & Oignons", nameEn: "Roasted Saint-Marcellin with Honey and Rosemary", price: "18€" },
      { name: "Huîtres Fines de Claire n°3 - 6 Pièces", nameEn: "Fine de Claire Oyster No.3", price: "22€" },
      { name: "Pizza à la Truffe", nameEn: "Crispy Truffle Pizza", price: "29€" },
      { name: "Caviar Osciètre 30g", nameEn: "", price: "105€" }
    ],
    entrees: [
      { name: "Stracciatella, pistaches concassées", nameEn: "Stracciatella, crushed pistachios", price: "18€" },
      { name: "Saumon Gravlax Créme Citronné", nameEn: "Slice of Salmon Gravlax", price: "22€" },
      { name: "Boeuf « Kyoto »", nameEn: "Kyoto Style Beef Carpaccio", price: "23€" },
      { name: "Tartare de Thon, Crème d'Avocat et Caviar de Mangue", nameEn: "Tuna Tartare, Avocado Cream & Mango", price: "24€" },
      { name: "Salade Caesar", nameEn: "", price: "24€" }
    ],
    pates: [
      { name: "Conchiglioni aux Morilles", nameEn: "Conchiglioni With Morels", price: "32€" },
      { name: "Linguine à la Truffe", nameEn: "Linguine With Truffle Sauce", price: "34€" }
    ],
    poissons: [
      { name: "Tentacules de Poulpe Grillé, Sauce Chimichurri", nameEn: "Grilled Octopus Tentacle, Chimichurri Sauce", price: "36€" },
      { name: "Filet de Loup, Sauce Vierge et Artichauts Frits", nameEn: "Seabass Fillet, Virgin Sauce", price: "39€" },
      { name: "Sole Meunière pour 2 personnes", nameEn: "Dover Sole Meunière for two", price: "49€/pers" }
    ],
    viandes: [
      { name: "Hamburger à la Truffe", nameEn: "", price: "32€" },
      { name: "Filet de Bœuf", nameEn: "Beef Fillet", price: "39€" },
      { name: "Entrecôte Angus 700g pour 2 personnes", nameEn: "Grilled Angus Ribeye 700 g for two", price: "49€/pers" }
    ],
    sauces: [
      { name: "Sauce Poivre", nameEn: "Pepper Sauce", price: "3€" },
      { name: "Sauce Morilles", nameEn: "Morels Sauce", price: "3€" }
    ],
    accompagnements: [
      { name: "Frites", nameEn: "French Fries", price: "8€" },
      { name: "Frites Truffe Parmesan", nameEn: "French Fries with Truffle", price: "12€" },
      { name: "Purée", nameEn: "Mashed Potatoes", price: "8€" },
      { name: "Légumes", nameEn: "Vegetables", price: "7€" },
      { name: "Coeur de Laitue", nameEn: "Heart of Lettuce", price: "6€" }
    ]
  };

  const CategorySection = ({ title, items, delay = 0 }) => (
    <div
      className={`transform transition-all duration-1000 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <h3 className="text-2xl font-serif font-bold text-black mb-6 uppercase tracking-wide border-b-2 border-black pb-2">
        {title}
      </h3>
      <div className="space-y-4 mb-12">
        {items.map((item, index) => (
          <div key={index} className="flex justify-between items-start">
            <div className="flex-1 pr-4">
              <h4 className="font-semibold text-black text-lg">{item.name}</h4>
              {item.nameEn && (
                <p className="text-gray-600 text-sm italic">{item.nameEn}</p>
              )}
            </div>
            <div className="font-bold text-black text-lg whitespace-nowrap">
              {item.price}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      id="carte"
      ref={sectionRef}
      className="py-24 md:py-32 bg-white relative"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div
            className={`transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-block bg-black text-white px-4 py-2 rounded-md text-sm font-semibold mb-6">
              <div className="flex items-center space-x-2">
                <ChefHat size={16} />
                <span>Notre Carte</span>
              </div>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-black mb-8 leading-tight">
              Notre sélection
            </h2>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-8">
          {/* Left Column */}
          <div>
            <CategorySection title="Entrées à partager" items={carte.entreesPartager} delay={100} />
            <CategorySection title="Entrées" items={carte.entrees} delay={200} />
            <CategorySection title="Pâtes" items={carte.pates} delay={300} />
          </div>

          {/* Right Column */}
          <div>
            <CategorySection title="Poissons" items={carte.poissons} delay={100} />
            <CategorySection title="Viandes" items={carte.viandes} delay={200} />
            <CategorySection title="Nos sauces" items={carte.sauces} delay={300} />
            <CategorySection title="Accompagnements" items={carte.accompagnements} delay={400} />
          </div>
        </div>

        {/* Footer Note */}
        <div
          className={`max-w-4xl mx-auto text-center mt-16 transform transition-all duration-1000 delay-500 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          <p className="text-gray-600 text-sm italic">
            Prix nets en euros - Service compris.<br />
            Merci de nous signaler toute allergie, intolérance ou régime alimentaire spécifique.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CarteSection;
